/**
 * 📦 V2 API client — base axios instance compartilhada pelos
 * services da nova hierarquia (products, physical-products, plans,
 * checkouts, sales, coupons, balances, withdrawals).
 *
 * Endpoints: /api/v2/*
 *
 * Diferenças do client V1 (services/api/products.js):
 * - Aceita resposta no padrão { success, ...payload, error?, code?, details? }
 * - Em erro estruturado V2 (code: VALIDATION_ERROR / NOT_FOUND / BUSINESS_RULE),
 *   rejeita com objeto enriquecido { code, message, details, status }
 * - Mantém interceptor de auth idêntico ao V1 (token em localStorage)
 */

import axios from 'axios';
import { API_URL } from '../../../config/api';
import { getToken, clearToken } from '../../../utils/auth';

const v2Api = axios.create({
  baseURL: `${API_URL}/api/v2`,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptor de autenticação — usa helper central (utils/auth.js)
v2Api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor de resposta — normaliza erros estruturados V2
v2Api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401: limpa auth + redirect (usa helper central)
    if (error.response?.status === 401) {
      clearToken();
      if (
        typeof window !== 'undefined'
        && window.location.pathname !== '/'
        && window.location.pathname !== '/login'
      ) {
        window.location.href = '/';
      }
    }

    // Enriquece o erro com o payload estruturado V2
    const data = error.response?.data;
    if (data && typeof data === 'object' && data.code) {
      return Promise.reject({
        code: data.code,
        message: data.error || data.message || 'Erro inesperado',
        details: data.details || null,
        status: error.response.status,
        original: error,
      });
    }
    return Promise.reject(error);
  },
);

export default v2Api;
